import { MouseEvent } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Divider,
  IconButton,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import { useAccount, useAccountSubscriptionPlanInfo, useAccountSubscriptionPrices } from 'src/hooks'
import { EPaymentMethod, IBillingModalFormValues, EAccountSubscriptionPlans } from 'src/types'

import { countPrice } from './utils'
import { useStyles } from './styles'
import { SubscriptionPlanPicker } from './subscription-plan-picker.component'
import { HelperWrapper } from 'src/components/helper-wrapper'

interface IBillingFormProps {
  onClose: () => void
  subscription_plan: EAccountSubscriptionPlans | null
}

const BillingForm = ({ onClose, subscription_plan }: IBillingFormProps) => {
  const [t] = useTranslation()
  const { data: prices } = useAccountSubscriptionPrices()
  const { values, setFieldValue } = useFormikContext<IBillingModalFormValues>()
  const { data: account } = useAccount()
  const { data: subscriptionPlanInfo, isLoading: issubScriptionPlanInfoLoading } =
    useAccountSubscriptionPlanInfo()
  const classes = useStyles()
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  if (!account) return null

  const handleSubscriptionChange = (
    _: MouseEvent<HTMLElement>,
    newSubscription: EAccountSubscriptionPlans,
  ) => {
    if (account.subscription_plan === newSubscription) return

    if (newSubscription !== null) {
      setFieldValue('subscription_plan', newSubscription)
    }
  }

  const handleMonthsChange = (_: MouseEvent<HTMLElement>, newMonths: number) => {
    if (newMonths !== null) {
      setFieldValue('months', newMonths)
    }
  }

  const handleMethodChange = (_: MouseEvent<HTMLElement>, newMethod: EPaymentMethod) => {
    if (newMethod !== null) {
      setFieldValue('method', newMethod)
    }
  }
  const total = countPrice(values.months, values.subscription_plan, prices ?? undefined)

  return (
    <div className={classes.formWrapper}>
      <IconButton className={classes.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>
      <div className={classes.upperWrapper}>
        <Typography className={classes.title}>
          <HelperWrapper t="subscribe-form-title" />
        </Typography>
        {(isTablet || !subscription_plan) && (
          <SubscriptionPlanPicker
            plusOptionDisabled={account.subscription_plan === EAccountSubscriptionPlans.plus}
            value={values.subscription_plan}
            handleChange={handleSubscriptionChange}
          />
        )}
        <div className={classes.itemWrapper}>
          <Typography className={classes.itemTitle}>
            <HelperWrapper t="subscribe-form-duration" />
          </Typography>
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={values.months}
            onChange={handleMonthsChange}
          >
            <ToggleButton value={1}>
              1&nbsp;
              <HelperWrapper t="subscription-month-text" />
            </ToggleButton>
            <ToggleButton value={12}>
              12&nbsp;
              <HelperWrapper t="subscription-months-text" />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <div className={classes.itemWrapper}>
          <Typography className={classes.itemTitle}>
            <HelperWrapper t="subscribe-form-method" />
          </Typography>
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={values.method}
            onChange={handleMethodChange}
          >
            <ToggleButton value={EPaymentMethod.Card}>
              <HelperWrapper t="subscribe-form-method-1" />
            </ToggleButton>
            <ToggleButton disabled value={EPaymentMethod.Crypto}>
              <HelperWrapper t="subscribe-form-method-2" />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>
      <div className={classes.lowerWrapper}>
        <div>
          <Divider />
          <div className={classes.paymentWrapper}>
            <Typography className={classes.subscriptionText}>
              <HelperWrapper t="subscribtion-text" />
            </Typography>
            <Typography className={classes.subscriptionText}>{values.subscription_plan}</Typography>
          </div>
          <div className={classes.paymentWrapper}>
            <Typography className={classes.paymentText}>
              <HelperWrapper t="total-text" />
            </Typography>
            {total ? (
              <Typography className={classes.paymentText}>
                <span style={{ fontWeight: 600 }}>{total.amount}</span> / {t(total.frequency)}
              </Typography>
            ) : (
              <Skeleton variant="text" width={40} height={24} />
            )}
          </div>
        </div>
        <Button variant="contained" type="submit">
          <HelperWrapper
            t={
              account.subscription_plan === EAccountSubscriptionPlans.plus
                ? 'upgrade-button-text'
                : 'proceed-button-text'
            }
          />
        </Button>
      </div>
    </div>
  )
}

export { BillingForm }
